import { BusinessObjectBase } from './base';
import { myUserTaskSchema } from '@schemas/business-objects/my-user-task';
import type { Role } from './role';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { User } from './user';
import type { Id, Json, UserTaskStatus } from '../base-types';

export interface MyUserTaskData {
  readonly id: string;
  readonly userTaskId: Id;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly userInterface?: string;
  readonly status?: UserTaskStatus;
  readonly data?: Json;
  readonly completedAt?: Date;
  readonly candidateUserId?: Id;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
  readonly roleId: Id;
  readonly role?: Role;
  readonly userId: Id;
  readonly user?: User;
  readonly tokenId: Id;
  readonly token?: Token;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface MyUserTask extends Readonly<MyUserTaskData> {}
type MyUserTaskCreateWithChildrenData = Partial<MyUserTaskData>;

export class MyUserTask extends BusinessObjectBase {
  static readonly entityName = 'MyUserTask' as const;
  static readonly plural = 'my-user-tasks';
  static schema = myUserTaskSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<MyUserTask[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new MyUserTask(data));
  }

  static async findOne(filter?: any): Promise<MyUserTask | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new MyUserTask(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<MyUserTask> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new MyUserTask(data);
  }

  static async create(data: Omit<MyUserTaskData, 'id'>): Promise<MyUserTask> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new MyUserTask(result);
  }

  static async createMany(
    data: Omit<MyUserTaskData, 'id'>[],
  ): Promise<MyUserTask[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MyUserTask(item));
  }

  static async createWithChildren(
    data: MyUserTaskCreateWithChildrenData,
  ): Promise<MyUserTask> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new MyUserTask(result);
  }

  static async createManyWithChildren(
    data: MyUserTaskCreateWithChildrenData[],
  ): Promise<MyUserTask[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MyUserTask(item));
  }

  static async updateById(
    id: string,
    updates: Partial<MyUserTaskData>,
  ): Promise<MyUserTask> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new MyUserTask(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
